/**
 * Full-world company profile HQ country select: platform-canonical overrides
 * plus every world ISO country (except blocked).
 *
 * Source of truth for:
 * - web HQ dropdown (`countriesData`)
 * - indirect verification allowlist
 * - phone default country ISO map from HQ name
 */

import { getIsoCountryCodeFromHeadquarters, WORLD_COUNTRY_ISO_CODES } from "./worldCountryIsoCodes";
import { getPrimaryRegistrationLabel } from "./registrationIdOptionsByCountry";

export type ProfileHeadquartersCountryItem = {
  name: string;
  registrationName: string;
  selectLabel?: string;
};

export type ProfileHeadquartersCountryGroup = {
  group: string;
  items: ProfileHeadquartersCountryItem[];
};

/** Banned / non-selectable HQ jurisdictions (ISO 3166-1 alpha-2). */
export const BLOCKED_HQ_ISO_CODES: ReadonlySet<string> = new Set(["KP", "PK"]);

/** Lowercase ISO 3166-1 alpha-2 for react-international-phone `excludedCountries`. */
export const BLOCKED_PHONE_ISO_CODES = ["pk", "kp"] as const;

/** E.164 country calling codes that must not be used to create an account. */
export const BLOCKED_PHONE_DIAL_CODES = ["+92", "+850"] as const;

export const BLOCKED_SIGNUP_COUNTRY_MESSAGE =
  "Registration from this country is not supported.";

/** Not a company jurisdiction (not useful as HQ). */
export const SKIPPED_NON_HQ_ISO_CODES: ReadonlySet<string> = new Set(["AQ"]);

/**
 * WORLD list labels superseded by platform-canonical HQ names.
 * USA, China splits, UAE keep product strings for DB compatibility.
 */
export const SKIP_WORLD_ISO_WHEN_PLATFORM_OVERRIDE: ReadonlySet<string> = new Set([
  "US",
  "CN",
  "HK",
  "AE",
]);

export const DEFAULT_REGISTRATION_NAME = "Company Registration Number";

export const PROFILE_HQ_GROUP_ORDER: readonly string[] = [
  "Asian Countries",
  "Middle East",
  "Europe",
  "Africa",
  "Oceania",
  "Americas",
  "Other",
] as const;

/** Existing product HQ entries (registration labels + canonical names). */
export const PLATFORM_HQ_OVERRIDES: readonly {
  group: string;
  iso?: string;
  name: string;
  registrationName: string;
  selectLabel?: string;
}[] = [
  { group: "Asian Countries", iso: "JP", name: "Japan", registrationName: "Japan Corporate number" },
  {
    group: "Asian Countries",
    iso: "TH",
    name: "Thailand",
    registrationName: "Corporate registration number",
  },
  {
    group: "Asian Countries",
    iso: "TW",
    name: "Taiwan",
    registrationName: "Unified Business Number (UBN)",
  },
  {
    group: "Asian Countries",
    iso: "SG",
    name: "Singapore",
    registrationName: "Corporate registration number",
  },
  {
    group: "Asian Countries",
    iso: "IN",
    name: "India",
    registrationName: "Corporate Identification Number (CIN)",
  },
  {
    group: "Asian Countries",
    iso: "KR",
    name: "South Korea",
    registrationName: "Business Registration Number (BRN)",
  },
  {
    group: "Asian Countries",
    iso: "LK",
    name: "Sri Lanka",
    registrationName: "Company Registration Number",
  },
  {
    group: "Asian Countries",
    iso: "MY",
    name: "Malaysia",
    registrationName: "Company Registration Number (SSM Number)",
  },
  {
    group: "Asian Countries",
    iso: "ID",
    name: "Indonesia",
    registrationName: "Business Identification Number (NIB)",
  },
  {
    group: "Asian Countries",
    iso: "VN",
    name: "Vietnam",
    registrationName: "Enterprise Registration Number (ERN)",
  },
  {
    group: "Asian Countries",
    iso: "PH",
    name: "Philippines",
    registrationName: "SEC Registration Number",
  },
  {
    group: "Asian Countries",
    iso: "CN",
    name: "China (Mainland)",
    registrationName: "Unified Social Credit Code (USCC)",
  },
  {
    group: "Asian Countries",
    iso: "HK",
    name: "China (Hong Kong)",
    registrationName: "Business Registration Number (BR No.)",
  },
  {
    group: "Asian Countries",
    iso: "BD",
    name: "Bangladesh",
    registrationName: "Registration Number (RJSC)",
  },
  {
    group: "Asian Countries",
    iso: "NP",
    name: "Nepal",
    registrationName: "Company Registration Number",
  },
  {
    group: "Asian Countries",
    iso: "BT",
    name: "Bhutan",
    registrationName: "Company Registration Number",
  },
  {
    group: "Middle East",
    iso: "AE",
    name: "United Arab Emirates (UAE)",
    registrationName: "Trade License Number",
  },
  {
    group: "Middle East",
    iso: "OM",
    name: "Oman",
    registrationName: "Commercial Registration Number",
  },
  {
    group: "Middle East",
    iso: "KW",
    name: "Kuwait",
    registrationName: "Commercial Registration Number",
  },
  {
    group: "Middle East",
    iso: "SA",
    name: "Saudi Arabia",
    registrationName: "Commercial Registration Number (CR No.)",
  },
  {
    group: "Middle East",
    iso: "TR",
    name: "Turkey",
    registrationName: "MERSIS Number",
  },
  {
    group: "Middle East",
    iso: "EG",
    name: "Egypt",
    registrationName: "Commercial Registration Number (CR No.)",
  },
  {
    group: "Middle East",
    iso: "QA",
    name: "Qatar",
    registrationName: "Commercial Registration Number",
  },
  {
    group: "Middle East",
    iso: "BH",
    name: "Bahrain",
    registrationName: "Commercial Registration Number (CR)",
  },
  {
    group: "Middle East",
    iso: "IL",
    name: "Israel",
    registrationName: "Company Registration Number",
  },
  {
    group: "Europe",
    iso: "AT",
    name: "Austria",
    registrationName: "Firmenbuchnummer (FN)",
  },
  {
    group: "Europe",
    iso: "BE",
    name: "Belgium",
    registrationName: "KBO / BCE (Enterprise Number)",
  },
  { group: "Europe", iso: "BG", name: "Bulgaria", registrationName: "UIC (ЕИК)" },
  {
    group: "Europe",
    iso: "HR",
    name: "Croatia",
    registrationName: "MBS (Commercial Court Registration Number)",
  },
  {
    group: "Europe",
    iso: "CY",
    name: "Cyprus",
    registrationName: "HE Number (Company Registration)",
  },
  {
    group: "Europe",
    iso: "CZ",
    name: "Czech Republic",
    registrationName: "IČO (Identification Number)",
  },
  { group: "Europe", iso: "DK", name: "Denmark", registrationName: "CVR Number" },
  {
    group: "Europe",
    iso: "EE",
    name: "Estonia",
    registrationName: "Registrikood (Registry Code)",
  },
  {
    group: "Europe",
    iso: "FI",
    name: "Finland",
    registrationName: "Business ID (Y-tunnus)",
  },
  {
    group: "Europe",
    iso: "FR",
    name: "France",
    registrationName: "SIREN / SIRET Number",
  },
  {
    group: "Europe",
    iso: "DE",
    name: "Germany",
    registrationName: "Handelsregisternummer (HRB / HRA)",
  },
  {
    group: "Europe",
    iso: "GR",
    name: "Greece",
    registrationName: "GEMI / Company Registration Number",
  },
  {
    group: "Europe",
    iso: "HU",
    name: "Hungary",
    registrationName: "Cégjegyzékszám (Company Registration Number)",
  },
  {
    group: "Europe",
    iso: "IE",
    name: "Ireland",
    registrationName: "CRO Registration Number",
  },
  {
    group: "Europe",
    iso: "IT",
    name: "Italy",
    registrationName: "REA Number / Company Registration Number",
  },
  {
    group: "Europe",
    iso: "LV",
    name: "Latvia",
    registrationName: "Company Registration Number (UR)",
  },
  {
    group: "Europe",
    iso: "LT",
    name: "Lithuania",
    registrationName: "Company Code (JAR)",
  },
  {
    group: "Europe",
    iso: "LU",
    name: "Luxembourg",
    registrationName: "RCS / Trade and Companies Register",
  },
  {
    group: "Europe",
    iso: "MT",
    name: "Malta",
    registrationName: "Company Registration Number",
  },
  {
    group: "Europe",
    iso: "NL",
    name: "Netherlands",
    registrationName: "Chamber of Commerce Number (KvK-nummer)",
  },
  { group: "Europe", iso: "NO", name: "Norway", registrationName: "Organisation Number" },
  { group: "Europe", iso: "PL", name: "Poland", registrationName: "KRS Number" },
  {
    group: "Europe",
    iso: "PT",
    name: "Portugal",
    registrationName: "NIPC (Company Tax ID)",
  },
  {
    group: "Europe",
    iso: "RO",
    name: "Romania",
    registrationName: "CUI / Trade Register Number",
  },
  {
    group: "Europe",
    iso: "RU",
    name: "Russia Federation",
    registrationName: "OGRN/INN",
  },
  {
    group: "Europe",
    iso: "SK",
    name: "Slovakia",
    registrationName: "IČO (Identification Number)",
  },
  {
    group: "Europe",
    iso: "SI",
    name: "Slovenia",
    registrationName: "Registration Number (AJPES)",
  },
  { group: "Europe", iso: "ES", name: "Spain", registrationName: "CIF / NIF" },
  { group: "Europe", iso: "SE", name: "Sweden", registrationName: "Organisation Number" },
  {
    group: "Europe",
    iso: "GB",
    name: "United Kingdom",
    registrationName: "Company Registration Number (CRN)",
    selectLabel: "United Kingdom (England, Wales, Northern Ireland, Scotland)",
  },
  {
    group: "Africa",
    iso: "ZA",
    name: "South Africa",
    registrationName: "Company Registration Number",
  },
  { group: "Africa", iso: "KE", name: "Kenya", registrationName: "Company Registration Number" },
  {
    group: "Africa",
    iso: "MA",
    name: "Morocco",
    registrationName: "Registre de Commerce (RC) / ICE",
  },
  { group: "Africa", iso: "NG", name: "Nigeria", registrationName: "RC Number (CAC)" },
  { group: "Oceania", iso: "AU", name: "Australia", registrationName: "ABN/ ACN" },
  { group: "Oceania", iso: "NZ", name: "New Zealand", registrationName: "NZBN/ NZCN" },
  { group: "Americas", iso: "US", name: "USA", registrationName: "State Entity Number" },
  { group: "Americas", iso: "CA", name: "Canada", registrationName: "Corporation Number" },
  { group: "Americas", iso: "MX", name: "Mexico", registrationName: "Folio Mercantil" },
  { group: "Americas", iso: "BR", name: "Brazil", registrationName: "CNPJ" },
];

const ASIA_ISO = new Set([
  "AF",
  "AM",
  "AZ",
  "BD",
  "BT",
  "BN",
  "KH",
  "CN",
  "GE",
  "HK",
  "IN",
  "ID",
  "JP",
  "KZ",
  "KP",
  "KR",
  "KG",
  "LA",
  "MO",
  "MY",
  "MV",
  "MN",
  "MM",
  "NP",
  "PK",
  "PH",
  "SG",
  "LK",
  "TW",
  "TJ",
  "TH",
  "TL",
  "TM",
  "UZ",
  "VN",
  "IO",
  "CX",
  "CC",
]);

const MIDDLE_EAST_ISO = new Set([
  "AE",
  "BH",
  "EG",
  "IR",
  "IQ",
  "IL",
  "JO",
  "KW",
  "LB",
  "OM",
  "PS",
  "QA",
  "SA",
  "SY",
  "TR",
  "YE",
]);

const EUROPE_ISO = new Set([
  "AL",
  "AD",
  "AT",
  "BY",
  "BE",
  "BA",
  "BG",
  "HR",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FO",
  "FI",
  "FR",
  "DE",
  "GI",
  "GR",
  "GG",
  "HU",
  "IS",
  "IE",
  "IM",
  "IT",
  "JE",
  "XK",
  "LV",
  "LI",
  "LT",
  "LU",
  "MK",
  "MT",
  "MD",
  "MC",
  "ME",
  "NL",
  "NO",
  "PL",
  "PT",
  "RO",
  "RU",
  "SM",
  "RS",
  "SK",
  "SI",
  "ES",
  "SJ",
  "SE",
  "CH",
  "UA",
  "GB",
  "VA",
]);

const AFRICA_ISO = new Set([
  "DZ",
  "AO",
  "BJ",
  "BW",
  "BF",
  "BI",
  "CV",
  "CM",
  "CF",
  "TD",
  "KM",
  "CD",
  "CG",
  "CI",
  "DJ",
  "EG",
  "GQ",
  "ER",
  "SZ",
  "ET",
  "GA",
  "GM",
  "GH",
  "GN",
  "GW",
  "KE",
  "LS",
  "LR",
  "LY",
  "MG",
  "MW",
  "ML",
  "MR",
  "MU",
  "YT",
  "MA",
  "MZ",
  "NA",
  "NE",
  "NG",
  "RE",
  "RW",
  "ST",
  "SN",
  "SC",
  "SL",
  "SO",
  "ZA",
  "SS",
  "SD",
  "TZ",
  "TG",
  "TN",
  "UG",
  "EH",
  "ZM",
  "ZW",
]);

const OCEANIA_ISO = new Set([
  "AS",
  "AU",
  "CK",
  "FJ",
  "PF",
  "GU",
  "KI",
  "MH",
  "FM",
  "NR",
  "NC",
  "NZ",
  "NU",
  "NF",
  "MP",
  "PW",
  "PG",
  "PN",
  "WS",
  "SB",
  "TK",
  "TO",
  "TV",
  "UM",
  "VU",
  "WF",
]);

const AMERICAS_ISO = new Set([
  "AI",
  "AG",
  "AR",
  "AW",
  "BS",
  "BB",
  "BZ",
  "BM",
  "BO",
  "BQ",
  "BR",
  "VG",
  "CA",
  "KY",
  "CL",
  "CO",
  "CR",
  "CU",
  "CW",
  "DM",
  "DO",
  "EC",
  "SV",
  "FK",
  "GF",
  "GL",
  "GD",
  "GP",
  "GT",
  "GY",
  "HT",
  "HN",
  "JM",
  "MQ",
  "MX",
  "MS",
  "NI",
  "PA",
  "PY",
  "PE",
  "PR",
  "BL",
  "KN",
  "LC",
  "MF",
  "PM",
  "VC",
  "SX",
  "GS",
  "SR",
  "TT",
  "TC",
  "US",
  "UY",
  "VE",
  "VI",
  "AN",
]);

/** Egypt is Middle East for product grouping (not Africa group). */
const PRIMARY_GROUP_EXCEPTIONS: Readonly<Record<string, string>> = {
  EG: "Middle East",
};

function regionGroupForIso(code: string): string {
  const upper = code.toUpperCase();
  if (PRIMARY_GROUP_EXCEPTIONS[upper]) return PRIMARY_GROUP_EXCEPTIONS[upper];
  if (MIDDLE_EAST_ISO.has(upper)) return "Middle East";
  if (ASIA_ISO.has(upper)) return "Asian Countries";
  if (EUROPE_ISO.has(upper)) return "Europe";
  if (AFRICA_ISO.has(upper)) return "Africa";
  if (OCEANIA_ISO.has(upper)) return "Oceania";
  if (AMERICAS_ISO.has(upper)) return "Americas";
  return "Other";
}

const CONTINENT_FROM_HQ_GROUP: Record<string, string> = {
  "Asian Countries": "Asia",
  "Middle East": "Asia",
  Europe: "Europe",
  Africa: "Africa",
  Oceania: "Oceania",
  Americas: "Americas",
};

/** Real-world continent for an HQ country name. Middle East rolls into Asia. */
export function continentForHeadquartersCountry(country: string): string | null {
  const iso = getIsoCountryCodeFromHeadquarters(country);
  if (!iso) return null;
  return CONTINENT_FROM_HQ_GROUP[regionGroupForIso(iso)] ?? null;
}

const SOUTH_AMERICA_ISO = new Set([
  "AR",
  "BO",
  "BR",
  "CL",
  "CO",
  "EC",
  "FK",
  "GF",
  "GY",
  "PY",
  "PE",
  "GS",
  "SR",
  "UY",
  "VE",
]);

export const GEOGRAPHY_REGION_ORDER = [
  "Asia",
  "Europe",
  "Africa",
  "North America",
  "South America",
  "Middle East",
  "Oceania",
] as const;

export type GeographyRegion = (typeof GEOGRAPHY_REGION_ORDER)[number];

/** Hero geography donut: Middle East stays separate; Americas split North vs South. */
export function geographyRegionForHeadquartersCountry(country: string): GeographyRegion | null {
  const iso = getIsoCountryCodeFromHeadquarters(country);
  if (!iso) return null;
  const group = regionGroupForIso(iso);
  if (group === "Asian Countries") return "Asia";
  if (group === "Middle East") return "Middle East";
  if (group === "Europe") return "Europe";
  if (group === "Africa") return "Africa";
  if (group === "Oceania") return "Oceania";
  if (group === "Americas") {
    return SOUTH_AMERICA_ISO.has(iso.toUpperCase()) ? "South America" : "North America";
  }
  return null;
}

function emptyGroups(): Map<string, ProfileHeadquartersCountryItem[]> {
  const map = new Map<string, ProfileHeadquartersCountryItem[]>();
  for (const g of PROFILE_HQ_GROUP_ORDER) {
    map.set(g, []);
  }
  return map;
}

function sortItems(items: ProfileHeadquartersCountryItem[]): ProfileHeadquartersCountryItem[] {
  return [...items].sort((a, b) => a.name.localeCompare(b.name, "en"));
}

/**
 * Grouped HQ options for profile select (platform overrides first historically,
 * then remaining world countries; sorted A–Z within each group).
 */
export function buildProfileHeadquartersCountryGroups(): ProfileHeadquartersCountryGroup[] {
  const groups = emptyGroups();
  const usedIso = new Set<string>();
  const usedNamesLower = new Set<string>();

  for (const item of PLATFORM_HQ_OVERRIDES) {
    const list = groups.get(item.group) ?? [];
    const primaryLabel = getPrimaryRegistrationLabel(item.name);
    list.push({
      name: item.name,
      registrationName: primaryLabel ?? item.registrationName,
      ...(item.selectLabel ? { selectLabel: item.selectLabel } : {}),
    });
    groups.set(item.group, list);
    usedNamesLower.add(item.name.toLowerCase());
    if (item.iso) usedIso.add(item.iso.toUpperCase());
  }

  for (const world of WORLD_COUNTRY_ISO_CODES) {
    const code = world.code.toUpperCase();
    if (BLOCKED_HQ_ISO_CODES.has(code)) continue;
    if (SKIPPED_NON_HQ_ISO_CODES.has(code)) continue;
    if (SKIP_WORLD_ISO_WHEN_PLATFORM_OVERRIDE.has(code)) continue;
    if (usedIso.has(code)) continue;
    if (usedNamesLower.has(world.name.toLowerCase())) continue;

    const group = regionGroupForIso(code);
    const list = groups.get(group) ?? groups.get("Other")!;
    const primaryLabel = getPrimaryRegistrationLabel(world.name);
    list.push({
      name: world.name,
      registrationName: primaryLabel ?? DEFAULT_REGISTRATION_NAME,
    });
    usedIso.add(code);
    usedNamesLower.add(world.name.toLowerCase());
  }

  return PROFILE_HQ_GROUP_ORDER.map((group) => ({
    group,
    items: sortItems(groups.get(group) ?? []),
  })).filter((g) => g.items.length > 0);
}

/** Flat HQ select values (`Company.headquartersCountry`). */
export function getAllProfileHeadquartersCountryNames(): string[] {
  return buildProfileHeadquartersCountryGroups().flatMap((g) => g.items.map((i) => i.name));
}

export function getProfileHeadquartersCountryNamesForGroup(group: string): string[] {
  const needle = group.trim().toLowerCase();
  if (!needle) return [];
  const found = buildProfileHeadquartersCountryGroups().find((g) => g.group.toLowerCase() === needle);
  return found ? found.items.map((item) => item.name) : [];
}

type SignupRegionAliasTarget =
  | (typeof PROFILE_HQ_GROUP_ORDER)[number]
  | "North America"
  | "South America";

const SIGNUP_REGION_ALIASES: Readonly<Record<string, SignupRegionAliasTarget>> = {
  europe: "Europe",
  asia: "Asian Countries",
  "asian countries": "Asian Countries",
  "middle east": "Middle East",
  africa: "Africa",
  oceania: "Oceania",
  americas: "Americas",
  america: "Americas",
  "north america": "North America",
  "south america": "South America",
};

function countryNamesForSignupRegion(region: SignupRegionAliasTarget): string[] {
  if (region === "North America" || region === "South America") {
    return getProfileHeadquartersCountryNamesForGroup("Americas").filter(
      (name) => geographyRegionForHeadquartersCountry(name) === region,
    );
  }
  if (region === "Other") return [];
  return getProfileHeadquartersCountryNamesForGroup(region);
}

/** User-facing continent labels for filter selects (not persisted as company HQ). */
const REGION_FILTER_DISPLAY_LABEL: Readonly<Record<Exclude<SignupRegionAliasTarget, "Other">, string>> = {
  Europe: "Europe",
  "Asian Countries": "Asia",
  Africa: "Africa",
  "Middle East": "Middle East",
  Oceania: "Oceania",
  Americas: "Americas",
  "North America": "North America",
  "South America": "South America",
};

function displayLabelForSignupRegion(region: SignupRegionAliasTarget): string | null {
  if (region === "Other") return null;
  return REGION_FILTER_DISPLAY_LABEL[region];
}

function pushUniqueName(out: string[], seen: Set<string>, name: string) {
  const key = name.toLowerCase();
  if (seen.has(key)) return;
  seen.add(key);
  out.push(name);
}

/**
 * Filter/select values: keep continent labels (Europe, Asia, …) then catalog countries.
 * Use for within-cluster country filters only — not company HQ signup.
 */
export function countryFilterSelectValues(names: string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();

  for (const raw of names) {
    const name = raw.trim();
    if (!name) continue;
    const region = SIGNUP_REGION_ALIASES[name.toLowerCase()];
    if (!region) continue;
    const countries = countryNamesForSignupRegion(region);
    if (countries.length === 0) continue;
    const label = displayLabelForSignupRegion(region);
    if (label) pushUniqueName(out, seen, label);
  }

  for (const country of expandSignupSupportedCountryNames(names)) {
    pushUniqueName(out, seen, country);
  }

  return out;
}

/** Continent label when `raw` is a region alias; otherwise first expanded country. */
export function preferredCountryFilterValue(raw?: string | null): string | null {
  const name = raw?.trim() ?? "";
  if (!name) return null;
  const region = SIGNUP_REGION_ALIASES[name.toLowerCase()];
  if (region) {
    const countries = countryNamesForSignupRegion(region);
    const label = displayLabelForSignupRegion(region);
    if (countries.length > 0 && label) return label;
  }
  return expandSignupSupportedCountryNames([name])[0] ?? name;
}

/**
 * Replace HQ region labels (Europe, Asia, …) with catalog country names.
 * Region labels themselves are not kept as selectable HQ values.
 */
export function expandSignupSupportedCountryNames(names: string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();

  const push = (name: string) => {
    const key = name.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(name);
  };

  for (const raw of names) {
    const name = raw.trim();
    if (!name) continue;
    const region = SIGNUP_REGION_ALIASES[name.toLowerCase()];
    if (region) {
      const countries = countryNamesForSignupRegion(region);
      if (countries.length > 0) {
        for (const country of countries) push(country);
        continue;
      }
    }
    push(name);
  }

  return out;
}

/**
 * ISO 3166-1 alpha-2 (lowercase) for react-international-phone defaultCountry
 * from headquarters country name.
 */
export function getHeadquartersCountryPhoneIsoMap(): Record<string, string> {
  const map: Record<string, string> = {};

  for (const item of PLATFORM_HQ_OVERRIDES) {
    if (item.iso) {
      map[item.name] = item.iso.toLowerCase();
    }
  }

  for (const world of WORLD_COUNTRY_ISO_CODES) {
    const code = world.code.toUpperCase();
    if (BLOCKED_HQ_ISO_CODES.has(code)) continue;
    if (SKIPPED_NON_HQ_ISO_CODES.has(code)) continue;
    if (SKIP_WORLD_ISO_WHEN_PLATFORM_OVERRIDE.has(code)) continue;
    if (map[world.name] !== undefined) continue;
    map[world.name] = code.toLowerCase();
  }

  return map;
}

/**
 * Lowercase ISO 3166-1 alpha-2 for `react-international-phone` defaultCountry.
 * Corridor signup may use "China" while the HQ catalog stores "China (Mainland)".
 */
export function phoneIsoForHeadquartersCountry(
  country?: string | null,
  fallback = "in",
): string {
  const raw = country?.trim();
  if (!raw) return fallback;

  const map = getHeadquartersCountryPhoneIsoMap();
  if (map[raw]) return map[raw];
  const lower = raw.toLowerCase();
  for (const [name, iso] of Object.entries(map)) {
    if (name.toLowerCase() === lower) return iso;
  }

  const fromHq = getIsoCountryCodeFromHeadquarters(raw);
  if (fromHq && !BLOCKED_HQ_ISO_CODES.has(fromHq.toUpperCase())) {
    return fromHq.toLowerCase();
  }
  return fallback;
}

export function isBlockedHeadquartersIso(code: string | null | undefined): boolean {
  if (!code?.trim()) return false;
  return BLOCKED_HQ_ISO_CODES.has(code.trim().toUpperCase());
}

export function isBlockedHeadquartersCountry(country: string | null | undefined): boolean {
  return isBlockedHeadquartersIso(getIsoCountryCodeFromHeadquarters(country));
}

export function normalizePhoneForBlockedCountryCheck(phone: string): string {
  const compact = phone.replace(/[^\d+]/g, "");
  if (compact.startsWith("00")) return `+${compact.slice(2)}`;
  if (compact.startsWith("+")) return compact;
  return `+${compact}`;
}

export function isBlockedSignupPhone(phone: string | null | undefined): boolean {
  if (!phone?.trim()) return false;
  const normalized = normalizePhoneForBlockedCountryCheck(phone);
  const codes = [...BLOCKED_PHONE_DIAL_CODES].sort((a, b) => b.length - a.length);
  return codes.some((code) => normalized === code || normalized.startsWith(code));
}
